import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'

export interface SavedViewTabConfig<TViewId extends string = string> {
  id: TViewId
  label: string
  description?: string
}

interface SavedViewTabsProps<TViewId extends string> {
  views: SavedViewTabConfig<TViewId>[]
  activeView: TViewId
  onViewChange: (viewId: TViewId) => void
  counts: Record<TViewId, number>
  isModified: boolean
  /** Views that should be disabled */
  disabledViews?: TViewId[]
}

export function SavedViewTabs<TViewId extends string>({
  views,
  activeView,
  onViewChange,
  counts,
  isModified,
  disabledViews = [],
}: SavedViewTabsProps<TViewId>) {
  return (
    <Tabs
      value={activeView}
      onValueChange={(v) => onViewChange(v as TViewId)}
      className="flex-shrink-0"
    >
      <TabsList>
        {views.map((view) => {
          const isDisabled = disabledViews.includes(view.id)
          const isActive = activeView === view.id

          return (
            <TabsTrigger
              key={view.id}
              value={view.id}
              disabled={isDisabled}
              className="gap-1.5"
              title={isDisabled ? 'Select a location to use this view' : view.description}
            >
              {/* Modified dot indicator */}
              {isActive && isModified && (
                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
              )}
              {view.label}
              <span
                className={cn(
                  'rounded-full px-1.5 py-0.5 text-xs font-medium',
                  isActive
                    ? 'bg-background text-foreground'
                    : 'bg-muted text-muted-foreground'
                )}
              >
                {counts[view.id]}
              </span>
            </TabsTrigger>
          )
        })}
      </TabsList>
    </Tabs>
  )
}
